/**
 * /email-preferences — where a notification email's "Manage notifications"
 * and "Stop emails like this" links land (SAD-954). Public: many recipients
 * are client contacts with no Specra account, so the signed token in the
 * link's fragment is the credential. The fragment never leaves the browser
 * (no server or edge log sees it); the token is sent only in request bodies.
 * Opening the page changes nothing — stopping takes one click, so a mail
 * scanner that prefetches links cannot unsubscribe anyone.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraButton, SpecraSelect, SpecraSpinner, SpecraWordmark } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { EMAIL_MODE_LABELS } from '../components/notifications/NotificationPreferencesTable';
export class EmailLinkInvalidError extends Error {
}
export async function postEmailPreferences(body) {
    const res = await fetch('/api/email-preferences', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
    });
    const data = await res.json().catch(()=>({}));
    if (res.status === 403 || data?.code === 'link_invalid') throw new EmailLinkInvalidError();
    if (!res.ok) throw new Error(data?.error || 'Could not update your preferences');
    return data;
}
/** `#token=…&stop=1` → its parts; an empty token when the link was mangled. */ export function emailLinkFromHash(hash) {
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    return {
        token: params.get('token') ?? '',
        stop: params.get('stop') === '1'
    };
}
const MODES = [
    'INSTANT',
    'DAILY_DIGEST',
    'OFF'
];
export default function EmailPreferencesPage() {
    const location = useLocation();
    const { token, stop } = useMemo(()=>emailLinkFromHash(location.hash), [
        location.hash
    ]);
    const [prefs, setPrefs] = useState(null);
    const [invalid, setInvalid] = useState(token === '');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [stopped, setStopped] = useState(false);
    const call = useCallback(async (body)=>{
        setBusy(true);
        setError(null);
        try {
            const next = await postEmailPreferences({
                token,
                ...body
            });
            setPrefs(next);
            return true;
        } catch (e) {
            if (e instanceof EmailLinkInvalidError) setInvalid(true);
            else setError(e instanceof Error ? e.message : 'Could not update your preferences');
            return false;
        } finally{
            setBusy(false);
        }
    }, [
        token
    ]);
    useEffect(()=>{
        if (token) void call({});
    }, [
        token,
        call
    ]);
    const linkKind = prefs?.kinds.find((k)=>k.kind === prefs.link_kind);
    const alreadyOff = linkKind?.mode === 'OFF';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: "Email notifications - Specra"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex min-h-dvh w-screen items-start justify-center bg-background px-4 py-12",
                "data-testid": Testids.emailPreferences.page,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-full max-w-lg flex-col gap-6",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center gap-2 text-center",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraWordmark, {
                                    height: 28,
                                    className: "mb-2"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-xl font-semibold",
                                    children: "Email notifications"
                                })
                            ]
                        }),
                        invalid ? /*#__PURE__*/ _jsxs("div", {
                            className: "rounded-md border px-4 py-3 text-sm",
                            "data-testid": Testids.emailPreferences.invalidState,
                            role: "alert",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "font-medium",
                                    children: "This link has expired or is not valid."
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-muted-foreground",
                                    children: "If you have a Specra account, sign in and open Settings → Notifications."
                                })
                            ]
                        }) : !prefs ? /*#__PURE__*/ _jsx("div", {
                            className: "flex justify-center",
                            children: /*#__PURE__*/ _jsx(SpecraSpinner, {})
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "rounded-md border px-4 py-4 text-sm",
                                    children: stopped || alreadyOff && !stop ? /*#__PURE__*/ _jsxs("p", {
                                        "data-testid": Testids.emailPreferences.stoppedState,
                                        children: [
                                            "You won't get ",
                                            /*#__PURE__*/ _jsx("strong", {
                                                children: prefs.link_kind_label
                                            }),
                                            " emails anymore. You can turn them back on below."
                                        ]
                                    }) : /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col gap-3",
                                        children: [
                                            /*#__PURE__*/ _jsxs("p", {
                                                children: [
                                                    "This email was about ",
                                                    /*#__PURE__*/ _jsx("strong", {
                                                        children: prefs.link_kind_label
                                                    }),
                                                    "."
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                disabled: busy || alreadyOff,
                                                onClick: async ()=>{
                                                    if (await call({
                                                        stop: true
                                                    })) setStopped(true);
                                                },
                                                "data-testid": Testids.emailPreferences.stopButton,
                                                children: alreadyOff ? `${prefs.link_kind_label} emails are off` : `Stop ${prefs.link_kind_label} emails`
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx("h4", {
                                            className: "text-sm font-semibold",
                                            children: "All email notifications"
                                        }),
                                        prefs.kinds.map((k)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between gap-4",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Label, {
                                                                htmlFor: `email-pref-${k.kind}`,
                                                                children: k.label
                                                            }),
                                                            /*#__PURE__*/ _jsxs("p", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: [
                                                                    "Default:",
                                                                    ' ',
                                                                    (EMAIL_MODE_LABELS[k.default_mode] ?? k.default_mode).toLowerCase()
                                                                ]
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraSelect, {
                                                        id: `email-pref-${k.kind}`,
                                                        size: "sm",
                                                        className: "w-40",
                                                        value: k.mode,
                                                        disabled: busy,
                                                        onChange: (e)=>void call({
                                                                changes: [
                                                                    {
                                                                        kind: k.kind,
                                                                        mode: e.target.value
                                                                    }
                                                                ]
                                                            }),
                                                        "data-testid": Testids.emailPreferences.emailSelect,
                                                        "data-kind": k.kind,
                                                        children: MODES.map((m)=>/*#__PURE__*/ _jsx("option", {
                                                                value: m,
                                                                children: EMAIL_MODE_LABELS[m]
                                                            }, m))
                                                    })
                                                ]
                                            }, k.kind))
                                    ]
                                }),
                                error && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-destructive",
                                    role: "alert",
                                    children: error
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-center text-xs text-muted-foreground",
                                    children: [
                                        "Have a Specra account?",
                                        ' ',
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: "/settings?section=notifications",
                                            className: "text-primary hover:underline",
                                            children: "Manage in-app and email notifications in Settings"
                                        }),
                                        "."
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
